import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { experienceApi, type Recognition } from "@/lib/api/earnings";
import { ApiRequestError } from "@/lib/api/client";
import { getErrorMessage } from "@/lib/api/errorMessage";
import { notify } from "@/lib/notify";
import { qk } from "@/lib/query-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Select, Textarea } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/EmptyState";
import { formatDateTime } from "@/lib/utils/format";

const MAX = 280;

/** Appreciation from a manager. Everyone sees what they received; only a
 * manager sees the form, and only for their own direct reports (the server
 * enforces that, this just hides what would be refused). */
export function RecognitionCard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery({ queryKey: qk.earnings.recognition, queryFn: experienceApi.recognition, gcTime: 5 * 60 * 1000 });
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");

  const send = useMutation({
    mutationFn: () => experienceApi.recognise(to, message.trim()),
    onSuccess: async () => {
      notify.success("Recognition sent.");
      setMessage("");
      await queryClient.invalidateQueries({ queryKey: qk.earnings.recognition });
    },
    onError: (err) => notify.error(getErrorMessage(err)),
  });

  if (isLoading) {
    return (
      <Card aria-busy="true" data-testid="recognition-loading">
        <CardBody className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-14 w-full" />
        </CardBody>
      </Card>
    );
  }
  if (error) {
    if (error instanceof ApiRequestError && (error.status === 403 || error.status === 404)) return null;
    return (
      <Card>
        <ErrorState title="Couldn't load recognition" onRetry={() => refetch()} className="py-8" />
      </Card>
    );
  }
  if (!data || !data.enabled) return null;

  const isManager = data.team.length > 0;
  const canSend = to !== "" && message.trim().length > 0 && message.length <= MAX;

  return (
    <Card data-testid="recognition-card" className="dark:border-slate-700 dark:bg-slate-900">
      <CardHeader>
        <CardTitle className="dark:text-slate-100">Recognition</CardTitle>
      </CardHeader>
      <CardBody className="space-y-5">
        {data.received.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="recognition-empty">
            Appreciation from your manager will appear here.
          </p>
        ) : (
          <ul className="space-y-3" data-testid="recognition-received">
            {data.received.slice(0, 5).map((r) => (
              <RecognitionRow key={r.id} r={r} who={r.from_name} />
            ))}
          </ul>
        )}

        {isManager && (
          <form
            className="space-y-3 border-t border-slate-100 pt-4 dark:border-slate-800"
            onSubmit={(ev) => {
              ev.preventDefault();
              if (canSend) send.mutate();
            }}
            data-testid="recognition-form"
          >
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Appreciate someone on your team</p>
            <Select label="Team member" value={to} onChange={(ev) => setTo(ev.target.value)}>
              <option value="">Choose a person</option>
              {data.team.map((m) => (
                <option key={m.employee_id} value={m.employee_id}>
                  {m.name} ({m.employee_code})
                </option>
              ))}
            </Select>
            <Textarea
              label="Message"
              rows={3}
              maxLength={MAX}
              value={message}
              onChange={(ev) => setMessage(ev.target.value)}
              hint={`${message.length}/${MAX}`}
            />
            <Button type="submit" size="sm" disabled={!canSend} loading={send.isPending}>
              Send recognition
            </Button>

            {data.given.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Recently sent</p>
                <ul className="mt-1 space-y-2" data-testid="recognition-given">
                  {data.given.slice(0, 3).map((r) => (
                    <RecognitionRow key={r.id} r={r} who={r.to_name ? `To ${r.to_name}` : undefined} />
                  ))}
                </ul>
              </div>
            )}
          </form>
        )}
      </CardBody>
    </Card>
  );
}

function RecognitionRow({ r, who }: { r: Recognition; who?: string }) {
  return (
    <li className="text-sm">
      <p className="text-slate-800 dark:text-slate-100">{r.message}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {who ? `${who} · ` : ""}
        {formatDateTime(r.created_at)}
      </p>
    </li>
  );
}
